import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import { TELEGRAM_API_URL, TELEGRAM_BOT_USERNAME } from '../config/contract';
import { formatUSDT, formatNumber, generateUserId } from '../utils/helpers';
import './TelegramNotify.css';

const TelegramNotify = ({ account, userDetails, onClose }) => {
  const toast = useToast();
  const [telegramId, setTelegramId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLinked, setIsLinked] = useState(false);

  useEffect(() => {
    const savedId = localStorage.getItem('adrenaline_telegram_id');
    if (savedId) {
      setTelegramId(savedId);
      setIsLinked(true);
    }
  }, []);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const generateReportData = () => {
    return {
      userId: generateUserId(account),
      walletAddress: account,
      totalInvested: formatUSDT(userDetails?.totalInvested || 0),
      totalEarned: formatUSDT(userDetails?.totalEarned || 0),
      pendingReinvest: formatUSDT(userDetails?.pendingReinvest || 0),
      directReferrals: userDetails?.directReferrals || '0',
      totalNFTs: userDetails?.totalNFTsPurchased || '0',
      isAutoUser: userDetails?.isAutoUser || false
    };
  };

  const sendTelegramReport = async () => {
    if (!telegramId.trim()) {
      toast.error('Please enter your Telegram Chat ID');
      return;
    }

    setIsSending(true);
    
    try {
      const reportData = generateReportData();
      
      const response = await fetch(TELEGRAM_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: telegramId,
          report: reportData
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Report sent to Telegram!');
        setIsLinked(true);
        localStorage.setItem('adrenaline_telegram_id', telegramId);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      toast.error(`Failed: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const copyBotUsername = () => {
    navigator.clipboard.writeText(TELEGRAM_BOT_USERNAME);
    toast.success('Bot username copied!');
  };

  return (
    <div className="telegram-overlay" onClick={handleClose}>
      <div className="telegram-modal glass-card" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={handleClose} type="button">✕</button>
        
        <div className="telegram-header">
          <img src="/tele.gif" alt="Telegram" className="telegram-gif" />
          <h2>Telegram Notifications</h2>
          <p>Get instant alerts for all your activities</p>
        </div>

        <div className="telegram-steps">
          <div className="step-item">
            <span className="step-number">1</span>
            <div className="step-content">
              <span className="step-title">Start our Bot</span>
              <span className="step-desc">Click to copy and search on Telegram:</span>
              <div className="bot-username-box">
                <code className="bot-username">{TELEGRAM_BOT_USERNAME}</code>
                <button className="copy-btn" onClick={copyBotUsername} type="button">📋</button>
              </div>
            </div>
          </div>
          
          <div className="step-item">
            <span className="step-number">2</span>
            <div className="step-content">
              <span className="step-title">Get Your Chat ID</span>
              <span className="step-desc">Send <code>/start</code> to get your Chat ID</span>
            </div>
          </div>
          
          <div className="step-item">
            <span className="step-number">3</span>
            <div className="step-content">
              <span className="step-title">Enter Chat ID Below</span>
              <span className="step-desc">Paste your Chat ID to enable notifications</span>
            </div>
          </div>
        </div>

        <div className="input-group">
          <label>Telegram Chat ID</label>
          <input
            type="text"
            className="cyber-input"
            placeholder="e.g., 123456789"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
          />
        </div>

        {isLinked && (
          <div className="linked-status">
            <span className="status-icon">✓</span>
            <span>Telegram linked! You'll receive notifications.</span>
          </div>
        )}

        <button 
          className="send-report-btn"
          onClick={sendTelegramReport}
          disabled={isSending || !telegramId.trim()}
          type="button"
        >
          {isSending ? (
            <>
              <span className="btn-spinner"></span>
              Sending...
            </>
          ) : isLinked ? (
            <>📤 Send Report Now</>
          ) : (
            <>🔗 Link & Enable Notifications</>
          )}
        </button>

        <div className="telegram-info">
          <p>🔔 Auto-notifications for:</p>
          <ul>
            <li>💎 NFT Purchases</li>
            <li>💰 NFT Sold & Profit</li>
            <li>🎯 Sponsor Income</li>
            <li>📊 Level Income</li>
            <li>🔄 Auto Reinvest</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Function to send activity notification
export const sendActivityNotification = async (activityType, amount, relatedAddress = null) => {
  const telegramId = localStorage.getItem('adrenaline_telegram_id');
  if (!telegramId) return;

  const typeLabels = {
    'PUR': '🛒 NFT Purchased',
    'SOLD': '💰 NFT Sold',
    'SPO': '🎯 Sponsor Income',
    'LVL': '📊 Level Income',
    'PRF': '💎 Profit Received',
    'PRI': '💵 Principal Returned',
    'PND': '⏳ Pending Reinvest',
    'REP': '🔄 Auto Reinvest',
    'REF': '↩️ Refunded',
    'WA': '💳 Withdrawn',
    'BRN': '🔥 NFT Burned',
    'REG': '📝 Registered'
  };

  const label = typeLabels[activityType] || activityType;
  const amountStr = parseFloat(amount) > 0 ? `$${formatNumber(parseFloat(amount))}` : '';
  
  let message = `*Adrenaline NFT Alert*\n\n`;
  message += `${label}\n`;
  if (amountStr) message += `Amount: ${amountStr} USDT\n`;
  message += `Time: ${new Date().toLocaleString()}\n`;
  message += `\n_Adrenaline NFT Platform_`;

  try {
    await fetch(TELEGRAM_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: telegramId,
        message: message,
        parse_mode: 'Markdown'
      })
    });
  } catch (error) {
    console.error('Failed to send telegram notification:', error);
  }
};

export default TelegramNotify;
